import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { ApiError, ApiNetworkError, ApiServerError } from '@/lib/api-client';
import { useLogin, useMe } from '@/features/auth/hooks';

/**
 * Login route — design ported 1:1 from `frontend/pages/login.html` so the
 * React shell visually matches the vanilla page during the migration. Dark
 * indigo radial background, brand strip with logo + tagline, white card with
 * deep shadow, Georgia-serif h1, "show/hide" text-button password toggle,
 * electric → navy gradient submit button.
 */

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email.'),
  password: z.string().min(1, 'Password is required'),
});
type LoginForm = z.infer<typeof loginSchema>;

const searchSchema = z.object({
  next: z.string().optional().catch(undefined),
  signedup: z.string().optional().catch(undefined),
  reset: z.string().optional().catch(undefined),
  // How many uploads were still running when the person signed out. Carried on the URL because
  // sign-out ends in a full-page redirect (the 401 guard reloads to /login), so an in-app toast
  // is torn down before it's seen — the notice has to be reconstructable after the reload.
  uploads_stopped: z.coerce.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});

export function LoginPage() {
  const { next, signedup, reset, uploads_stopped: uploadsStopped } = Route.useSearch();
  const me = useMe();
  const login = useLogin();
  const [showPw, setShowPw] = useState(false);
  const [alert, setAlert] = useState<AuthAlertState>(
    reset === '1'
      ? { kind: 'success', text: 'Password updated. Please log in with your new password.' }
      : signedup === '1'
        ? { kind: 'success', text: 'Account created. Please log in with your new password.' }
        : uploadsStopped
          ? {
              kind: 'info',
              text:
                uploadsStopped === 1
                  ? '1 upload was still in progress and was stopped when you signed out.'
                  : `${uploadsStopped} uploads were still in progress and were stopped when you signed out.`,
            }
          : null,
  );

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (me.data) {
      const dest = next && next.startsWith('/') ? next : '/dashboard';
      window.location.replace(dest);
    }
  }, [me.data, next]);

  const onSubmit = async (values: LoginForm) => {
    setAlert(null);
    try {
      await login.mutateAsync(values);
      const dest = next && next.startsWith('/') ? next : '/dashboard';
      window.location.assign(dest);
    } catch (err) {
      setAlert({ kind: 'error', text: loginErrorMessage(err) });
    }
  };

  return (
    <AuthShell>
      <h1
        className="text-navy text-[34px] leading-tight m-0 mb-1.5"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        Welcome back
      </h1>
      <p className="text-muted text-[15px] m-0 mb-[26px]">
        Log in to your REGARA account
      </p>

      <AuthAlert alert={alert} />

      <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <AuthField label="Email Address" htmlFor="email" required>
          <AuthInput
            id="email"
            type="email"
            autoComplete="username"
            placeholder="name@yourcompany.com"
            {...form.register('email')}
          />
          <FieldError message={form.formState.errors.email?.message} />
        </AuthField>

        <AuthField label="Password" htmlFor="password" required>
          <PasswordWrap show={showPw} onToggle={() => setShowPw((s) => !s)}>
            <AuthInput
              id="password"
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your password"
              {...form.register('password')}
            />
          </PasswordWrap>
          <FieldError message={form.formState.errors.password?.message} />
        </AuthField>

        <div className="flex items-center justify-between -mt-1.5 mb-[18px]">
          <span />
          <Link
            to="/forgot-password"
            className="text-brand-blue text-sm font-semibold no-underline hover:underline"
          >
            Forgot password?
          </Link>
        </div>

        <label className="flex items-center gap-2.5 text-sm text-navy mt-1 mb-5 cursor-pointer">
          <input type="checkbox" className="size-4 accent-electric" />
          Remember me
        </label>

        <AuthButton type="submit" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Log In'}
        </AuthButton>
      </form>

      <p className="mt-[22px] text-sm text-muted text-center m-0">
        Have an invite link?{' '}
        <Link
          to="/signup"
          className="text-brand-blue font-semibold no-underline hover:underline"
        >
          Set up your account
        </Link>
      </p>

      {me.data && <Redirecting />}
    </AuthShell>
  );
}

function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiNetworkError) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (err instanceof ApiServerError) {
    return 'Something went wrong on our end. Please try again in a moment.';
  }
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "We couldn't sign you in. Double-check your email and password, then try again. Forgot your password? Reset it below.";
    }
    if (err.status === 429) {
      return 'Too many sign-in attempts. Please wait a moment and try again.';
    }
    return err.message;
  }
  return 'Could not sign in. Please try again.';
}

// ── Shared shell + primitives (consumed by signup.tsx as well) ──────────

export type AuthAlertState = { kind: 'error' | 'success' | 'info'; text: string } | null;

/**
 * AuthShell — full-bleed indigo gradient background, stacked brand block
 * (wordmark over the uppercase tagline), centered white card. Used by both
 * `/login` and `/signup` so the chrome is identical across both pages.
 */
export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="min-h-screen w-full flex flex-col items-center px-5 pt-12 pb-16"
      style={{
        background:
          'radial-gradient(120% 120% at 50% 0%, var(--color-indigo-bg-1) 0%, var(--color-indigo-bg-2) 70%)',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="flex flex-col items-center gap-2.5 mb-7 text-[#cfd8e6]">
        <img src="/regara_logo_1.png" alt="REGARA" className="h-9 w-auto block" />
        <span className="text-[12px] uppercase tracking-[0.22em] text-[#aab6cc]">
          Clinical &amp; Regulatory Platform
        </span>
      </div>

      <main
        className="bg-white w-full max-w-[460px] rounded-[18px] pt-10 px-[38px] pb-9 relative"
        style={{ boxShadow: '0 24px 60px rgba(8,14,35,0.35)' }}
      >
        {children}
      </main>
    </div>
  );
}

export function AuthAlert({ alert }: { alert: AuthAlertState }) {
  if (!alert) return null;
  return (
    <div
      role="alert"
      className={cn(
        'rounded-[10px] py-3 px-3.5 text-sm mb-5 border',
        alert.kind === 'error'
          ? 'bg-state-error-bg text-state-error-text border-state-error-border'
          : alert.kind === 'info'
            ? 'bg-state-info-bg text-state-info-text border-state-info-border'
            : 'bg-state-success-bg text-state-success-text border-state-success-border',
      )}
    >
      {alert.text}
    </div>
  );
}

export function AuthField({
  label,
  htmlFor,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5">
      <label htmlFor={htmlFor} className="block font-semibold text-sm text-navy mb-2">
        {label}
        {required && <span className="text-error ml-[3px]">*</span>}
      </label>
      {children}
    </div>
  );
}

export const AuthInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className={cn(
      'w-full text-[15px] text-ink bg-white py-[13px] px-3.5 border border-auth-input-border rounded-[12px]',
      'placeholder:text-[#9aa6b6]',
      'focus:outline-none focus:border-electric focus:ring-[3px] focus:ring-[rgba(0,192,255,0.18)]',
      'transition-[border-color,box-shadow] duration-150',
      props.className,
    )}
  />
);

/**
 * PasswordWrap — relative container that overlays a text "show/hide" toggle
 * over the input. Mirrors the legacy approach: plain text button, not an
 * icon.
 */
export function PasswordWrap({
  show,
  onToggle,
  children,
}: {
  show: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      {children}
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 bg-transparent border-0 text-muted text-[13px] p-1.5 leading-none cursor-pointer hover:text-navy"
      >
        {show ? 'hide' : 'show'}
      </button>
    </div>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1.5 text-[13px] text-error m-0">{message}</p>;
}

/**
 * AuthButton — full-width submit button with the legacy electric→navy
 * horizontal gradient.
 */
export function AuthButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        'w-full py-[15px] px-[18px] border-0 cursor-pointer rounded-[12px] text-white text-base font-bold',
        'hover:brightness-[1.05] active:translate-y-px disabled:opacity-60 disabled:cursor-not-allowed',
        'transition-[filter,transform] duration-150',
        props.className,
      )}
      style={{
        background:
          'linear-gradient(90deg, #5bb8e8 0%, #2c7fb8 55%, var(--color-navy) 100%)',
        ...props.style,
      }}
    />
  );
}

function Redirecting() {
  return (
    <div
      className="absolute inset-0 bg-white/60 flex items-center justify-center text-muted text-sm rounded-[18px]"
      aria-hidden
    >
      Redirecting…
    </div>
  );
}
